import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AlertTriangle, Image as ImageIcon, MapPin, User as UserIcon, Phone, Globe, Wrench } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTS = [
  { v: "nuevo", l: "Nuevo", cls: "lx-badge-error" },
  { v: "en_revision", l: "En revisión", cls: "lx-badge-warning" },
  { v: "resuelto", l: "Resuelto", cls: "lx-badge-success" },
  { v: "cerrado", l: "Cerrado", cls: "lx-badge-muted" },
];

const PRIORITY_OPTS = [
  { v: "normal", l: "Normal" },
  { v: "medio", l: "Medio" },
  { v: "alto", l: "Alto" },
];

const TYPE_LABELS = {
  falta_insumos: "Falta de insumos",
  dano: "Daño",
  basura: "Basura excesiva",
  fuga: "Fuga",
  mal_olor: "Mal olor",
  acceso: "Acceso bloqueado",
  otro: "Otro",
};

const SOURCE_LABELS = {
  internal: { l: "Personal interno", icon: UserIcon, color: "#475569" },
  public: { l: "Reporte público", icon: Globe, color: "#0E7490" },
  checkin: { l: "Desde check-in", icon: Wrench, color: "#7C3AED" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-MX", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }); }
  catch { return iso; }
};

export default function IncidentsPage() {
  const { user } = useAuth();
  const isClient = user?.role === "cliente";
  const canEdit = !isClient && (user?.role === "admin" || user?.role === "supervisor");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", priority: "", client_id: "", location_id: "", area_id: "" });
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      const [cli, loc, ar] = await Promise.all([api.get("/clients"), api.get("/locations"), api.get("/areas")]);
      setClients(cli.data); setLocations(loc.data); setAreas(ar.data);
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const { data } = await api.get("/incidents", { params });
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const filteredLocs = useMemo(() => filters.client_id ? locations.filter(l => l.client_id === filters.client_id) : locations, [locations, filters.client_id]);
  const filteredAreas = useMemo(() => filters.location_id ? areas.filter(a => a.location_id === filters.location_id) : areas, [areas, filters.location_id]);

  const counts = useMemo(() => {
    const c = { total: items.length, nuevo: 0, en_revision: 0, resuelto: 0, cerrado: 0 };
    items.forEach(i => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div className="space-y-6">
      <div>
        <div className="lx-overline">Operación</div>
        <h1 className="lx-section-title mt-1">Incidencias y reportes</h1>
        <p className="text-lx-muted text-sm mt-1">Tickets generados por check-ins, personal y reportes públicos (QR sin login).</p>
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button onClick={() => setFilters({...filters, status: ""})} className={`lx-stat text-left ${!filters.status ? "ring-2 ring-lx-primary" : ""}`} data-testid="filter-status-all">
          <div className="lx-overline">Todas</div>
          <div className="font-heading font-bold text-2xl mt-1">{counts.total}</div>
        </button>
        {STATUS_OPTS.map(s => (
          <button key={s.v} onClick={() => setFilters({...filters, status: s.v})} className={`lx-stat text-left ${filters.status === s.v ? "ring-2 ring-lx-primary" : ""}`} data-testid={`filter-status-${s.v}`}>
            <div className="lx-overline">{s.l}</div>
            <div className="font-heading font-bold text-2xl mt-1">{counts[s.v] || 0}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="lx-card">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {!isClient && (
            <div>
              <label className="lx-label">Cliente</label>
              <select className="lx-input" value={filters.client_id} onChange={(e) => setFilters({...filters, client_id: e.target.value, location_id: "", area_id: ""})}>
                <option value="">Todos</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="lx-label">Sucursal</label>
            <select className="lx-input" value={filters.location_id} onChange={(e) => setFilters({...filters, location_id: e.target.value, area_id: ""})}>
              <option value="">Todas</option>
              {filteredLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Área</label>
            <select className="lx-input" value={filters.area_id} onChange={(e) => setFilters({...filters, area_id: e.target.value})}>
              <option value="">Todas</option>
              {filteredAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Prioridad</label>
            <select className="lx-input" value={filters.priority} onChange={(e) => setFilters({...filters, priority: e.target.value})}>
              <option value="">Todas</option>
              {PRIORITY_OPTS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="lx-card p-0 overflow-hidden">
        {loading ? <div className="p-6 text-lx-muted">Cargando…</div> :
         items.length === 0 ? <div className="p-10 text-center text-lx-muted">Sin incidencias con los filtros aplicados.</div> : (
          <div className="overflow-x-auto">
            <table className="lx-table">
              <thead>
                <tr><th>Fecha</th><th>Área / Sucursal</th><th>Tipo</th><th>Origen</th><th>Prioridad</th><th>Estatus</th></tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const src = SOURCE_LABELS[it.source] || SOURCE_LABELS.internal;
                  return (
                    <tr key={it.id} onClick={() => setDetail(it)} className="cursor-pointer" data-testid={`incident-row-${it.id}`}>
                      <td className="text-lx-muted text-xs">{fmt(it.created_at)}</td>
                      <td>
                        <div className="font-medium">{it.area_name}</div>
                        <div className="text-xs text-lx-muted flex items-center gap-1"><MapPin size={11}/>{it.location_name}</div>
                      </td>
                      <td>{TYPE_LABELS[it.type] || it.type}</td>
                      <td>
                        <span className="lx-badge" style={{ background: src.color + "22", color: src.color }}>
                          <src.icon size={11}/> {src.l}
                        </span>
                      </td>
                      <td>
                        <span className={`lx-badge ${it.priority === "alto" ? "lx-badge-error" : it.priority === "medio" ? "lx-badge-warning" : "lx-badge-muted"}`}>
                          {PRIORITY_OPTS.find(p => p.v === it.priority)?.l || it.priority}
                        </span>
                      </td>
                      <td>
                        <span className={`lx-badge ${STATUS_OPTS.find(s => s.v === it.status)?.cls || "lx-badge-muted"}`}>
                          {STATUS_OPTS.find(s => s.v === it.status)?.l || it.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && <DetailModal item={detail} onClose={() => setDetail(null)} canEdit={canEdit} onUpdated={(u) => { setDetail(u); load(); }}/>}
    </div>
  );
}

function DetailModal({ item, onClose, canEdit, onUpdated }) {
  const [status, setStatus] = useState(item.status);
  const [priority, setPriority] = useState(item.priority);
  const [resNotes, setResNotes] = useState(item.resolution_notes || "");
  const [saving, setSaving] = useState(false);
  const src = SOURCE_LABELS[item.source] || SOURCE_LABELS.internal;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/incidents/${item.id}`, { status, priority, resolution_notes: resNotes });
      onUpdated(data);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-lx-border flex items-center gap-2">
          <AlertTriangle size={18} className="text-amber-500"/>
          <h3 className="font-heading font-bold text-lg">Detalle de incidencia</h3>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="lx-overline">{TYPE_LABELS[item.type] || item.type}</div>
            <p className="mt-2 text-sm">{item.description}</p>
          </div>
          <div className="text-xs grid grid-cols-2 gap-2">
            <div><strong>Área:</strong> {item.area_name}</div>
            <div><strong>Sucursal:</strong> {item.location_name}</div>
            <div><strong>Origen:</strong> <span style={{ color: src.color }}>{src.l}</span></div>
            <div><strong>Fecha:</strong> {fmt(item.created_at)}</div>
            {item.reporter_name && <div><strong>Reportado por:</strong> {item.reporter_name}</div>}
            {item.reporter_contact && <div className="flex items-center gap-1"><Phone size={11}/>{item.reporter_contact}</div>}
          </div>
          {item.photo_url && (
            <div>
              <div className="lx-label">Evidencia</div>
              <img src={`${BACKEND_URL}${item.photo_url}`} alt="Evidencia" className="rounded-lg border border-lx-border max-h-64"/>
            </div>
          )}
          {canEdit ? (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="lx-label">Estatus</label>
                  <select className="lx-input" value={status} onChange={(e)=>setStatus(e.target.value)} data-testid="incident-status-select">
                    {STATUS_OPTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="lx-label">Prioridad</label>
                  <select className="lx-input" value={priority} onChange={(e)=>setPriority(e.target.value)} data-testid="incident-priority-select">
                    {PRIORITY_OPTS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="lx-label">Notas de resolución</label>
                <textarea className="lx-input" rows={3} value={resNotes} onChange={(e)=>setResNotes(e.target.value)} data-testid="incident-resolution-notes"/>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="lx-btn lx-btn-secondary">Cerrar</button>
                <button onClick={save} disabled={saving} className="lx-btn lx-btn-primary" data-testid="incident-save">{saving ? "Guardando…" : "Guardar"}</button>
              </div>
            </>
          ) : (
            <div className="flex justify-end"><button onClick={onClose} className="lx-btn lx-btn-secondary">Cerrar</button></div>
          )}
        </div>
      </div>
    </div>
  );
}
